// "Export stok" — the current per-lot stock of one event as .xlsx.
//
// Used on the stock page and inside the close-event dialog: the moment before
// closing is the last chance to keep a record of what went in, what sold and
// what is left, and a spreadsheet is what the warehouse counts against.

import { useState } from 'react';
import { FileSpreadsheet } from 'lucide-react';
import { Button, type ButtonProps } from '@/components/ui/Button';
import { downloadBlob } from '@/lib/api/invoice';
import { buildEventStockXlsx, eventStockFilename } from '@/lib/api/eventStockExport';
import { useProducts } from '@/hooks/useProducts';
import type { EventStockAllocation } from '@/types/domain';

export function ExportStockButton({
  event,
  allocations,
  size = 'md',
  variant = 'secondary',
}: {
  event: { name: string; code: string };
  allocations: EventStockAllocation[];
  size?: ButtonProps['size'];
  variant?: ButtonProps['variant'];
}) {
  const [busy, setBusy] = useState(false);
  // For the "Untuk import" sheet and its SKU dropdown.
  const { data: products } = useProducts({ isActive: true });
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      downloadBlob(await buildEventStockXlsx(event, allocations, products ?? []), eventStockFilename(event));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Export gagal');
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="inline-flex flex-col items-end">
      <Button
        variant={variant}
        size={size}
        leftIcon={<FileSpreadsheet size={size === 'sm' ? 14 : 16} />}
        onClick={run}
        loading={busy}
        disabled={allocations.length === 0 || !products}
        title={allocations.length === 0 ? 'Belum ada stok untuk diexport' : 'Unduh stok per lot (.xlsx) — termasuk sheet untuk diubah dan di-import balik'}
      >
        Export stok
      </Button>
      {error && <span className="text-caption text-danger mt-1">{error}</span>}
    </span>
  );
}
